/**
 * Amazon OAuth schemas — shared between API and web.
 */
import { z } from "zod";

// ─── Enums ──────────────────────────────────────────────────────

export const amazonAccountStatusSchema = z.enum(["connected", "token_invalid", "disconnected"]);
export const amazonAccountTypeSchema = z.enum(["seller", "vendor"]);

export type AmazonAccountStatus = z.infer<typeof amazonAccountStatusSchema>;

// ─── Request Schemas ────────────────────────────────────────────

export const amazonCallbackRequestSchema = z.object({
  code: z.string().min(1, "Authorization code is required"),
  state: z.string().min(1, "State parameter is required"),
});

export type AmazonCallbackRequest = z.infer<typeof amazonCallbackRequestSchema>;

export const selectProfileRequestSchema = z
  .object({
    profileIds: z.array(z.string().min(1)).min(1, "At least one profile is required"),
    state: z.string().min(1, "State is required"),
  })
  .transform((value) => ({
    // Dedupe: a repeated id would hit the profile_id unique constraint
    // mid-transaction and surface as a 500 instead of connecting once.
    profileIds: [...new Set(value.profileIds)],
    state: value.state,
  }));

export type SelectProfileRequest = z.infer<typeof selectProfileRequestSchema>;

// ─── Response Schemas ───────────────────────────────────────────

export const amazonProfileSchema = z.object({
  profileId: z.string(),
  marketplaceId: z.string(),
  marketplaceName: z.string(),
  countryCode: z.string(),
  name: z.string(),
  accountType: amazonAccountTypeSchema.nullable().optional(),
  /**
   * Amazon seller/vendor entity id, straight from the Ads profile — available
   * before any Shop exists, which is what lets onboarding group profiles into
   * Selling Partner authorizations. Null for profiles Amazon returns without
   * `accountInfo`; a vendor profile carries a non-Selling-Partner id here.
   */
  merchantId: z.string().nullable().optional(),
  /** Already bound in AdPraxis (selecting it joins the existing shop instead of creating one). */
  alreadyConnected: z.boolean().optional(),
  /** Bound by a shop the caller is not a member of. */
  connectedByOther: z.boolean().optional(),
});

export type AmazonProfile = z.infer<typeof amazonProfileSchema>;

export const amazonStatusResponseSchema = z.object({
  connected: z.boolean(),
  accountId: z.string().nullable(),
  profileId: z.string().nullable(),
  marketplaceId: z.string().nullable(),
  marketplaceName: z.string().nullable(),
  status: amazonAccountStatusSchema.nullable(),
  accountType: amazonAccountTypeSchema.nullable(),
  lastSyncAt: z.string().nullable(),
  connectedAt: z.string().nullable(),
  productCount: z.number().optional(),
});

export type AmazonStatusResponse = z.infer<typeof amazonStatusResponseSchema>;

export const amazonConnectResponseSchema = z.object({
  redirectUrl: z.string(),
});

export type AmazonConnectResponse = z.infer<typeof amazonConnectResponseSchema>;

export const amazonCallbackSelectProfileSchema = z.object({
  type: z.literal("select_profile"),
  selectionState: z.string(),
  profiles: z.array(amazonProfileSchema),
  /** Amazon login that completed the grant — shown back to the seller as account context. */
  accountEmail: z.string().nullable().optional(),
});

export type AmazonCallbackSelectProfile = z.infer<typeof amazonCallbackSelectProfileSchema>;

// Callback always returns select_profile — shop creation is deferred to
// POST /auth/amazon/select-profile so the user can confirm the profile.
export const amazonCallbackResponseSchema = amazonCallbackSelectProfileSchema;

export type AmazonCallbackResponse = z.infer<typeof amazonCallbackResponseSchema>;

export const amazonSelectProfileResponseSchema = z.object({
  shops: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  amazonAccounts: z.array(
    z.object({
      id: z.string(),
      profileId: z.string(),
      marketplaceName: z.string(),
      status: amazonAccountStatusSchema,
    }),
  ),
});

export type AmazonSelectProfileResponse = z.infer<typeof amazonSelectProfileResponseSchema>;

export const amazonProfilesResponseSchema = z.object({
  profiles: z.array(amazonProfileSchema),
  accountEmail: z.string().nullable().optional(),
});

export type AmazonProfilesResponse = z.infer<typeof amazonProfilesResponseSchema>;

// ─── Sync status (ADP-12 slice 5b) ──────────────────────────────

export const syncJobTypeSchema = z.enum([
  "sync:structure",
  "sync:reports-daily",
  "sync:search-terms",
  "sync:products",
  "sync:initial-backfill",
]);

export const syncRunSchema = z.object({
  // Optional for rolling deploys where a new Web bundle may briefly read an
  // older API pod that predates this additive field.
  runId: z.string().uuid().optional(),
  jobType: syncJobTypeSchema,
  reportType: z
    .enum(["campaign", "target", "searchTerm", "placement", "advertisedProduct"])
    .nullable()
    .optional(),
  reportStartDate: z.string().nullable().optional(),
  reportEndDate: z.string().nullable().optional(),
  status: z.enum(["running", "success", "failed", "partial", "skipped"]),
  records: z.number(),
  error: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
});

export type SyncRun = z.infer<typeof syncRunSchema>;

export const syncStatusResponseSchema = z.object({
  connected: z.boolean(),
  runs: z.array(syncRunSchema),
  running: z.boolean(),
  latestFailure: syncRunSchema.nullable(),
});

export type SyncStatusResponse = z.infer<typeof syncStatusResponseSchema>;

// ─── Durable Sync Batch status (ADP-93) ───────────────────────

export const syncSourceSchema = z.enum(["structure", "performance", "search_terms", "products"]);
export type SyncSource = z.infer<typeof syncSourceSchema>;

export const syncProgressStateSchema = z.enum(["planned", "running", "success", "failed"]);

export const syncSourceProgressSchema = z.object({
  source: syncSourceSchema,
  status: syncProgressStateSchema,
  profileCount: z.number().int().nonnegative(),
  syncingProfileCount: z.number().int().nonnegative(),
  completedProfileCount: z.number().int().nonnegative(),
  failedProfileCount: z.number().int().nonnegative(),
  plannedUnits: z.number().int().nonnegative(),
  completedUnits: z.number().int().nonnegative(),
  failedUnits: z.number().int().nonnegative(),
  percentage: z.number().int().min(0).max(100),
  error: z.string().nullable(),
});
export type SyncSourceProgress = z.infer<typeof syncSourceProgressSchema>;

export const syncDataFreshnessSchema = z.enum(["fresh", "stale", "unavailable"]);
export type SyncDataFreshness = z.infer<typeof syncDataFreshnessSchema>;

/** Data coverage, distinct from whether any sync worker completed successfully. */
export const syncPerformanceFreshnessSchema = z.object({
  lastSuccessfulAt: z.string().nullable(),
  dataThrough: z.string().nullable(),
  freshness: syncDataFreshnessSchema,
});
export type SyncPerformanceFreshness = z.infer<typeof syncPerformanceFreshnessSchema>;

export const syncProfileFreshnessSchema = z.object({
  shopId: z.string().uuid(),
  performance: syncPerformanceFreshnessSchema,
});
export type SyncProfileFreshness = z.infer<typeof syncProfileFreshnessSchema>;

export const syncSurfaceStateSchema = z.enum([
  "idle",
  "healthy",
  "syncing",
  "failed",
  "reconnect_required",
  "disconnected",
]);

export const syncAccountStatusSchema = z.object({
  connectionId: z.string().uuid(),
  state: syncSurfaceStateSchema,
  profileCount: z.number().int().nonnegative(),
  syncingProfileCount: z.number().int().nonnegative(),
  failedProfileCount: z.number().int().nonnegative(),
  percentage: z.number().int().min(0).max(100),
  plannedUnits: z.number().int().nonnegative(),
  completedUnits: z.number().int().nonnegative(),
  activeBatchCount: z.number().int().nonnegative(),
  latestBatchId: z.string().uuid().nullable(),
  retryBatchId: z.string().uuid().nullable(),
  retryableBatchCount: z.number().int().nonnegative(),
  failedSource: syncSourceSchema.nullable(),
  error: z.string().nullable(),
  lastSuccessfulAt: z.string().nullable(),
  performance: syncPerformanceFreshnessSchema,
  profiles: z.array(syncProfileFreshnessSchema),
  sources: z.array(syncSourceProgressSchema),
});
export type SyncAccountStatus = z.infer<typeof syncAccountStatusSchema>;

export const workspaceSyncStatusSchema = z.object({
  workspaceId: z.string().uuid(),
  state: syncSurfaceStateSchema,
  profileCount: z.number().int().nonnegative(),
  syncingProfileCount: z.number().int().nonnegative(),
  failedProfileCount: z.number().int().nonnegative(),
  failedAccountCount: z.number().int().nonnegative(),
  percentage: z.number().int().min(0).max(100),
  plannedUnits: z.number().int().nonnegative(),
  completedUnits: z.number().int().nonnegative(),
  activeBatchCount: z.number().int().nonnegative(),
  latestBatchId: z.string().uuid().nullable(),
  retryBatchId: z.string().uuid().nullable(),
  retryableBatchCount: z.number().int().nonnegative(),
  failedSource: syncSourceSchema.nullable(),
  error: z.string().nullable(),
  lastSuccessfulAt: z.string().nullable(),
  performance: syncPerformanceFreshnessSchema,
  sources: z.array(syncSourceProgressSchema),
  accounts: z.array(syncAccountStatusSchema),
});
export type WorkspaceSyncStatus = z.infer<typeof workspaceSyncStatusSchema>;

export const syncSseEventTypeSchema = z.enum([
  "batch.started",
  "source.progress",
  "source.completed",
  "batch.failed",
  "batch.completed",
]);
export type SyncSseEventType = z.infer<typeof syncSseEventTypeSchema>;

export const syncSseEventSchema = z.object({
  id: z.string().min(1),
  type: syncSseEventTypeSchema,
  workspaceId: z.string().uuid(),
  batchId: z.string().uuid(),
  source: syncSourceSchema.nullable(),
  occurredAt: z.string(),
});
export type SyncSseEvent = z.infer<typeof syncSseEventSchema>;

export const syncRunChangedEventSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  type: z.literal("run.changed"),
  shopId: z.string().uuid(),
  runId: z.string().uuid(),
  jobType: syncJobTypeSchema,
  status: z.enum(["running", "success", "failed", "partial", "skipped"]),
  occurredAt: z.string().datetime(),
});
export type SyncRunChangedEvent = z.infer<typeof syncRunChangedEventSchema>;
